/**
 * Project Fiber Usage Dialog - Display fiber usage statistics for entire project
 */
import React, { useEffect, useState } from 'react';
import { ProjectFiberUsage } from '../../types';
import { apiClient } from '../../services/api';
import './Dialog.css';
import './ProjectFiberUsageDialog.css';

interface ProjectFiberUsageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
}

export const ProjectFiberUsageDialog: React.FC<ProjectFiberUsageDialogProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const [usage, setUsage] = useState<ProjectFiberUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && projectId) {
      loadFiberUsage();
    }
  }, [isOpen, projectId]);

  const loadFiberUsage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.projects.getFiberUsage(projectId);
      setUsage(response.data);
    } catch (err: any) {
      console.error('[ProjectFiberUsageDialog] Error loading fiber usage:', err);
      setError(err.response?.data?.detail || 'Nepodarilo sa načítať štatistiky');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog project-fiber-usage-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📊 Využitie vlákien v projekte</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="dialog-content">
          {loading && (
            <div className="loading-message">
              ⏳ Načítavam štatistiky...
            </div>
          )}

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {usage && !loading && (
            <>
              {/* Overall Statistics */}
              <div className="overall-stats">
                <h3>Celkové štatistiky</h3>
                <div className="stats-grid">
                  <div className="stat-box">
                    <div className="stat-label">Celkový počet káblov</div>
                    <div className="stat-value">{usage.total_cables}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Celkový počet vlákien</div>
                    <div className="stat-value">{usage.total_fibers}</div>
                  </div>
                </div>
              </div>

              {/* Usage Breakdown */}
              <div className="usage-breakdown">
                <h3>Rozdelenie využitia</h3>
                <div className="breakdown-cards">
                  <div className="breakdown-card fully-used">
                    <div className="card-icon">✅</div>
                    <div className="card-content">
                      <div className="card-label">Použité vlákna</div>
                      <div className="card-value">
                        {usage.used_fibers}
                        <span className="card-percent">({usage.statistics.used_percent}%)</span>
                      </div>
                      <div className="card-description">Pripojené na oboch koncoch</div>
                    </div>
                  </div>

                  <div className="breakdown-card free-ends">
                    <div className="card-icon">⚠️</div>
                    <div className="card-content">
                      <div className="card-label">Polospojené vlákna</div>
                      <div className="card-value">
                        {usage.half_connected_fibers}
                        <span className="card-percent">({usage.statistics.half_connected_percent}%)</span>
                      </div>
                      <div className="card-description">Pripojené len na jednom konci</div>
                    </div>
                  </div>

                  <div className="breakdown-card unused">
                    <div className="card-icon">🟢</div>
                    <div className="card-content">
                      <div className="card-label">Voľné vlákna</div>
                      <div className="card-value">
                        {usage.free_fibers}
                        <span className="card-percent">({usage.statistics.free_percent}%)</span>
                      </div>
                      <div className="card-description">Nepripojené, dostupné pre použitie</div>
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div className="usage-chart">
                  <div className="chart-bar">
                    {usage.statistics.used_percent > 0 && (
                      <div
                        className="chart-segment fully-used"
                        style={{ width: `${usage.statistics.used_percent}%` }}
                        title={`Použité: ${usage.statistics.used_percent}%`}
                      />
                    )}
                    {usage.statistics.half_connected_percent > 0 && (
                      <div
                        className="chart-segment free-ends"
                        style={{ width: `${usage.statistics.half_connected_percent}%` }}
                        title={`Polospojené: ${usage.statistics.half_connected_percent}%`}
                      />
                    )}
                    {usage.statistics.free_percent > 0 && (
                      <div
                        className="chart-segment unused"
                        style={{ width: `${usage.statistics.free_percent}%` }}
                        title={`Voľné: ${usage.statistics.free_percent}%`}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Cables with Half-Connected Fibers */}
              {usage.cables_with_half_connected.length > 0 && (
                <div className="cables-section">
                  <h3>⚠️ Káble s polospojenými vláknami (Top 10)</h3>
                  <div className="cables-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Kábel</th>
                          <th>Polospojené</th>
                          <th>Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usage.cables_with_half_connected.map((cable) => (
                          <tr key={cable.cable_id}>
                            <td className="cable-name">{cable.cable_name}</td>
                            <td className="free-ends-count">{cable.half_connected_count}</td>
                            <td className="free-ends-detail">
                              {cable.half_connected_in_from_pole > 0 && (
                                <span className="detail-item">
                                  Voľný koniec v {cable.from_pole_name}: {cable.half_connected_in_from_pole}
                                </span>
                              )}
                              {cable.half_connected_in_to_pole > 0 && (
                                <span className="detail-item">
                                  Voľný koniec v {cable.to_pole_name}: {cable.half_connected_in_to_pole}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Cables by Usage */}
              {usage.cables_by_usage.length > 0 && (
                <div className="cables-section">
                  <h3>📉 Káble s najnižším využitím (Top 10)</h3>
                  <div className="cables-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Kábel</th>
                          <th>Vlákna</th>
                          <th>Použité</th>
                          <th>Využitie</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usage.cables_by_usage.map((cable) => (
                          <tr key={cable.cable_id}>
                            <td className="cable-name">{cable.cable_name}</td>
                            <td>{cable.fiber_count}</td>
                            <td>{cable.used_count}</td>
                            <td>
                              <div className="usage-bar-container">
                                <div
                                  className="usage-bar"
                                  style={{ width: `${cable.usage_percent}%` }}
                                />
                                <span className="usage-percent">{cable.usage_percent}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="dialog-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Zavrieť
          </button>
        </div>
      </div>
    </div>
  );
};

